import { useId, useMemo } from 'react';
import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Spinner,
  Textarea,
} from '@monksflow/monks-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import {
  getCategoryDisplayLabel,
  getSpringboardTabById,
  SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
  SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH,
  SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
  toSpringboardCategoryOverrides,
  useSaveSpringboardCategories,
  type SpringboardCustomizationFormCategory,
} from '@/features/springboard';
import { notificationService } from '@/lib/services/notification.service';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
const buildFormSchema = (t: TFunction<'admin'>) =>
  z.object({
    label: z
      .string()
      .trim()
      .max(
        SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH,
        t('admin.customization.labelMaxLength', { max: SPRINGBOARD_CATEGORY_LABEL_MAX_LENGTH }),
      ),
    subtitle: z
      .string()
      .trim()
      .max(
        SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
        t('admin.customization.subtitleMaxLength', {
          max: SPRINGBOARD_CATEGORY_SUBTITLE_MAX_LENGTH,
        }),
      ),
    description: z
      .string()
      .trim()
      .max(
        SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
        t('admin.customization.descriptionMaxLength', {
          max: SPRINGBOARD_CATEGORY_DESCRIPTION_MAX_LENGTH,
        }),
      ),
  });

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

/* eslint-disable i18next/no-literal-string -- form field names; labelKey carries the copy */
const FIELDS = [
  { name: 'label', labelKey: 'admin.customization.label', multiline: false },
  { name: 'subtitle', labelKey: 'admin.customization.subtitle', multiline: false },
  { name: 'description', labelKey: 'admin.customization.description', multiline: true },
] as const;
/* eslint-enable i18next/no-literal-string */

interface CategoryEditSheetProps {
  open: boolean;
  /** Every category as stored. The API replaces all four at once, so a save resends the rest. */
  categories: SpringboardCustomizationFormCategory[];
  categoryId: SpringboardCustomizationFormCategory['id'];
  onClose: () => void;
}

/**
 * Edits one category's copy. The host remounts this on each open (a `key` it bumps), which
 * seeds the form from the stored values and drops an abandoned draft; closing leaves it
 * mounted so the exit animation plays over the values it held.
 */
export function CategoryEditSheet({
  open,
  categories,
  categoryId,
  onClose,
}: CategoryEditSheetProps) {
  const { t } = useTranslation('admin');
  const fieldId = useId();
  const saveMutation = useSaveSpringboardCategories();

  const stored = categories.find((category) => category.id === categoryId);
  const defaults = getSpringboardTabById(categoryId);

  const formSchema = useMemo(() => buildFormSchema(t), [t]);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: stored?.label ?? '',
      subtitle: stored?.subtitle ?? '',
      description: stored?.description ?? '',
    },
    mode: 'onTouched',
  });

  const isSaving = saveMutation.isPending;

  const handleSubmit = form.handleSubmit((values) => {
    const next = categories.map((category) =>
      category.id === categoryId ? { ...category, ...values } : category,
    );
    saveMutation.mutate(toSpringboardCategoryOverrides(next), {
      onSuccess: () => {
        notificationService.showSuccess(t('admin.customization.customizationSaved'));
        onClose();
      },
    });
  });

  return (
    <FormSidePanel
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('admin.customization.editCategoryTitle', {
        category: getCategoryDisplayLabel(categoryId, stored?.label),
      })}
      description={t('admin.customization.about.emptyFieldsUseDefault')}
      showCloseButton={!isSaving}
      narrow
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose} disabled={isSaving}>
            {t('admin.customization.cancel')}
          </Button>
          <Button size="lg" onClick={() => void handleSubmit()} disabled={isSaving}>
            {isSaving && <Spinner aria-hidden />}
            {isSaving ? t('admin.customization.saving') : t('admin.customization.saveChanges')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-md"
      >
        {FIELDS.map(({ name, labelKey, multiline }) => {
          const error = form.formState.errors[name];
          const id = `${fieldId}-${name}`;
          const inputProps = {
            id,
            ...form.register(name),
            placeholder: defaults?.[name],
            disabled: isSaving,
            'aria-invalid': Boolean(error) || undefined,
          };
          return (
            <Field key={name} data-invalid={Boolean(error) || undefined}>
              <FieldLabel htmlFor={id}>{t(labelKey)}</FieldLabel>
              {multiline ? <Textarea {...inputProps} rows={5} /> : <Input {...inputProps} />}
              {error && <FieldError>{error.message}</FieldError>}
            </Field>
          );
        })}
      </form>
    </FormSidePanel>
  );
}
